// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useNotify, usePermissions, useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import PageHeader from '../components/PageHeader'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { dateInputToDobSeconds } from '../pets/dob'
import { PET_SPECIES_VALUES } from '../types'
import { authHeaders, EMAIL_RE } from '../utils/api'

/**
 * Clinic-visit pre-registration: a vet registers a pet owner on the owner's
 * behalf (POST /api/users/register-owner), optionally together with the pet
 * and an EMR note documenting the current visit. The owner receives a claim
 * email; the registering vet is associated automatically and gains access to
 * the owner's pets.
 *
 * Structure rules:
 *  - owner details required (name + email)
 *  - pet section optional
 *  - visit-note section only available when a pet is being registered
 *    (EMRs attach to pets — a note with no pet has nowhere to live)
 */

interface SuccessData {
  owner: { id: number | string; email: string; name: string }
  pet?: { id: number | string; name?: string }
  emr?: { id: number | string }
  claimEmailSent: boolean
}

const emptyForm = {
  ownerName: '',
  ownerEmail: '',
  ownerPhone: '',
  petName: '',
  petSpecies: 'dogs',
  petBreed: '',
  petDob: '',
  noteText: '',
}

const RegisterOwner = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const navigate = useNavigate()
  const { permissions } = usePermissions()

  const [form, setForm] = useState(emptyForm)
  const [addPet, setAddPet] = useState(false)
  const [addNote, setAddNote] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState<SuccessData | null>(null)
  // Existing-account collision: the backend recorded an owner-link-request
  // and emailed the owner for consent (202 APPROVAL_REQUESTED).
  const [approvalRequested, setApprovalRequested] = useState<{
    approvalEmailSent: boolean
  } | null>(null)

  if (!['vet', 'vetadmin'].includes(permissions?.role)) {
    return (
      <Box>
        <Alert severity="warning">{translate('pos.register_owner.not_authorized')}</Alert>
      </Box>
    )
  }

  const set = (field: keyof typeof emptyForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }))

  const handleTogglePet = (checked: boolean) => {
    setAddPet(checked)
    if (!checked) {
      // The note rides on the pet — dropping the pet drops the note
      setAddNote(false)
      setForm((f) => ({ ...f, petName: '', petBreed: '', petDob: '', noteText: '' }))
    }
  }

  const handleSubmit = async () => {
    setError('')

    const ownerName = form.ownerName.trim()
    const ownerEmail = form.ownerEmail.trim().toLowerCase()
    if (ownerName.length < 2) {
      setError(translate('pos.register_owner.error_name_required'))
      return
    }
    if (!EMAIL_RE.test(ownerEmail)) {
      setError(translate('pos.register_owner.error_email_invalid'))
      return
    }
    if (addNote && !form.noteText.trim()) {
      setError(translate('pos.register_owner.error_note_empty'))
      return
    }

    const body: Record<string, unknown> = {
      owner: {
        name: ownerName,
        email: ownerEmail,
        ...(form.ownerPhone.trim() ? { phone: form.ownerPhone.trim() } : {}),
      },
    }
    const petDobSeconds = dateInputToDobSeconds(form.petDob)
    if (addPet) {
      body.pet = {
        ...(form.petName.trim() ? { name: form.petName.trim() } : {}),
        species: form.petSpecies,
        ...(form.petBreed.trim() ? { breed: form.petBreed.trim() } : {}),
        // Backend stores dob as unix SECONDS. This POST bypasses the
        // dataProvider, so the plausibility bound has to be applied here too —
        // a six-digit year from the date input would otherwise register a pet
        // born in the year 202020. dateInputToDobSeconds yields undefined for
        // anything unusable; an unusable dob is simply not sent.
        ...(typeof petDobSeconds === 'number' ? { dob: petDobSeconds } : {}),
      }
      if (addNote) {
        body.emr = { notes: form.noteText.trim() }
      }
    }

    setSubmitting(true)
    try {
      const response = await deadlineFetch('/api/users/register-owner', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      })
      const data = await response.json()

      if (response.status === 201 && data?.success) {
        setSuccess(data.data)
      } else if (response.status === 202 && data?.code === 'APPROVAL_REQUESTED') {
        setApprovalRequested({ approvalEmailSent: data?.data?.approvalEmailSent !== false })
      } else if (response.status === 409) {
        const messageKey =
          data?.code === 'APPROVAL_PENDING'
            ? 'pos.register_owner.approval_pending'
            : data?.code === 'ALREADY_LINKED'
              ? 'pos.register_owner.already_linked'
              : 'pos.register_owner.owner_exists'
        setError(translate(messageKey))
      } else {
        setError(data?.error || translate('pos.register_owner.error_generic'))
      }
    } catch (_e) {
      notify('pos.register_owner.error_network', { type: 'error' })
      setError(translate('pos.register_owner.error_network'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleRegisterAnother = () => {
    setForm(emptyForm)
    setAddPet(false)
    setAddNote(false)
    setError('')
    setSuccess(null)
    setApprovalRequested(null)
  }

  if (approvalRequested) {
    return (
      <Box sx={{ maxWidth: 640 }}>
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              {translate('pos.register_owner.approval_title')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {translate('pos.register_owner.approval_body')}
            </Typography>
            <Alert
              severity={approvalRequested.approvalEmailSent ? 'info' : 'warning'}
              sx={{ my: 2 }}
            >
              {approvalRequested.approvalEmailSent
                ? translate('pos.register_owner.approval_email_sent')
                : translate('pos.register_owner.approval_email_failed')}
            </Alert>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" onClick={handleRegisterAnother}>
                {translate('pos.register_owner.register_another')}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/owners')}>
                {translate('pos.register_owner.view_owners')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    )
  }

  if (success) {
    return (
      <Box sx={{ maxWidth: 640 }}>
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              {translate('pos.register_owner.success_title')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {translate('pos.register_owner.success_owner', {
                name: success.owner.name,
                email: success.owner.email,
              })}
            </Typography>
            {success.pet && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                {translate('pos.register_owner.success_pet', { name: success.pet.name || '' })}
              </Typography>
            )}
            <Alert severity={success.claimEmailSent ? 'success' : 'warning'} sx={{ my: 2 }}>
              {success.claimEmailSent
                ? translate('pos.register_owner.email_sent', { email: success.owner.email })
                : translate('pos.register_owner.email_failed')}
            </Alert>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" onClick={handleRegisterAnother}>
                {translate('pos.register_owner.register_another')}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/owners')}>
                {translate('pos.register_owner.view_owners')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <PageHeader
        title={translate('pos.register_owner.title')}
        subtitle={translate('pos.register_owner.subtitle')}
      />
      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Owner details */}
          <TextField
            label={translate('pos.register_owner.owner_name')}
            value={form.ownerName}
            onChange={set('ownerName')}
            fullWidth
            required
            margin="dense"
            disabled={submitting}
          />
          <TextField
            label={translate('pos.register_owner.owner_email')}
            value={form.ownerEmail}
            onChange={set('ownerEmail')}
            type="email"
            fullWidth
            required
            margin="dense"
            disabled={submitting}
          />
          <TextField
            label={translate('pos.register_owner.owner_phone')}
            value={form.ownerPhone}
            onChange={set('ownerPhone')}
            fullWidth
            margin="dense"
            disabled={submitting}
          />

          <Divider sx={{ my: 2 }} />

          {/* Optional pet */}
          <FormControlLabel
            control={
              <Checkbox
                checked={addPet}
                onChange={(e) => handleTogglePet(e.target.checked)}
                disabled={submitting}
              />
            }
            label={translate('pos.register_owner.add_pet')}
          />
          {addPet && (
            <Box sx={{ pl: 4 }}>
              <TextField
                label={translate('pos.register_owner.pet_name')}
                value={form.petName}
                onChange={set('petName')}
                fullWidth
                margin="dense"
                disabled={submitting}
              />
              <TextField
                label={translate('pos.register_owner.pet_species')}
                value={form.petSpecies}
                onChange={set('petSpecies')}
                select
                SelectProps={{ native: true }}
                fullWidth
                margin="dense"
                disabled={submitting}
              >
                {/* Same source as PetEdit (vic/vetpo#8). This is the CREATION
                    path, so a narrow list here mints the mislabelled record
                    that #8's edit-side fix then has to repair: a vet
                    registering a guinea pig had to pick Dog or Cat. Labels are
                    translated rather than hardcoded English, as everywhere
                    else in this form. */}
                {PET_SPECIES_VALUES.map((id) => (
                  <option key={id} value={id}>
                    {translate(`resources.pets.species_options.${id}`)}
                  </option>
                ))}
              </TextField>
              <TextField
                label={translate('pos.register_owner.pet_breed')}
                value={form.petBreed}
                onChange={set('petBreed')}
                fullWidth
                margin="dense"
                disabled={submitting}
              />
              <TextField
                label={translate('pos.register_owner.pet_dob')}
                value={form.petDob}
                onChange={set('petDob')}
                type="date"
                fullWidth
                margin="dense"
                InputLabelProps={{ shrink: true }}
                disabled={submitting}
              />

              {/* Optional visit note — only offered alongside a pet */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={addNote}
                    onChange={(e) => {
                      setAddNote(e.target.checked)
                      if (!e.target.checked) setForm((f) => ({ ...f, noteText: '' }))
                    }}
                    disabled={submitting}
                  />
                }
                label={translate('pos.register_owner.add_note')}
                sx={{ mt: 1 }}
              />
              {addNote && (
                <TextField
                  label={translate('pos.register_owner.note_text')}
                  value={form.noteText}
                  onChange={set('noteText')}
                  multiline
                  minRows={3}
                  fullWidth
                  margin="dense"
                  disabled={submitting}
                />
              )}
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} /> : undefined}
            >
              {translate('pos.register_owner.submit')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default RegisterOwner
